import type { Chamber, TargetCoverageDto } from '../../../types/lobbyDay'

/**
 * Ingested (LegiScan) districts arrive already prefixed — `HD-001`, `SD-013`, `HD-VA-8` — while
 * manual rows are bare (`72`, `Fifth`). Strip any leading chamber token (`HD/SD/CD/LD-`), an
 * optional embedded 2-letter state (`-VA-`), and leading zeros on a numeric remainder, so the
 * label can apply exactly one canonical prefix. Named districts (`Fifth`, `Third Middlesex`) pass
 * through untouched. (Display-only; matching still goes through the backend DistrictMatcher.)
 */
export function normalizeDistrict(district: string): string {
  const stripped = district.trim().replace(/^(?:HD|SD|CD|LD)-(?:[A-Za-z]{2}-)?/i, '')
  const numeric = stripped.match(/^0*(\d+[A-Za-z]?)$/)
  return numeric ? numeric[1] : stripped
}

export function chamberDistrictLabel(chamber: Chamber, district: string): string {
  if (chamber === 'USSenate') return 'U.S. Senate'
  const chamberWord = chamber === 'StateSenate' ? 'Senate' : chamber === 'StateHouse' ? 'House' : 'Congress'
  const code = chamber === 'StateSenate' ? 'SD' : chamber === 'StateHouse' ? 'HD' : 'CD'
  const d = normalizeDistrict(district)
  if (!d) return chamberWord
  // Numeric districts get the chamber code (HD-1); named districts show bare (Third Middlesex).
  return /^\d+[A-Za-z]?$/.test(d) ? `${chamberWord} · ${code}-${d}` : `${chamberWord} · ${d}`
}

export function effectiveTargetRows(rows: TargetCoverageDto[], targetAll: boolean): TargetCoverageDto[] {
  return targetAll ? rows : rows.filter((r) => r.isTarget)
}

export function coverageSummary(rows: TargetCoverageDto[], targetAll: boolean) {
  const eff = effectiveTargetRows(rows, targetAll)
  return {
    targets: eff.length,
    covered: eff.filter((r) => !r.isGap).length,
    gaps: eff.filter((r) => r.isGap).length,
    priorityGaps: eff.filter((r) => r.isGap && r.isPriority).length,
  }
}
